import { DEFAULT_DESCRIPTIVE_COLUMN_LIMIT } from '@components/bid_request/details/constants';
import { Lot } from 'types';

/**
 * Filters custom column headers during import to enforce descriptive column limits.
 *
 * During imports, all headers are DESCRIPTIVE columns (not qualifying/LIQ columns).
 * This function limits the number of descriptive columns based on column_order_index
 * and filters associated custom column items to match retained headers.
 *
 * Note: Qualifying columns (Line Item Questions) are created separately by buyers
 * and are never part of import data.
 *
 * @param importedLots - Lots created from import (containing only descriptive columns)
 * @param maxColumnLimit - Maximum number of descriptive columns allowed (defaults to 10)
 * @returns The same array of lots with filtered descriptive columns
 */
export const applyDescriptiveColumnLimits = (
  importedLots: Lot[],
  maxColumnLimit = DEFAULT_DESCRIPTIVE_COLUMN_LIMIT
): Lot[] => {
  // Intentionally mutating input data for performance optimization with large datasets
  // Creating deep copies would be memory intensive during bulk line item imports
  // eslint-disable-next-line no-param-reassign
  importedLots.forEach((lot: Lot) => {
    // Filter headers to only include those within the column limit
    // All headers from import are descriptive columns
    const limitedHeaders = lot.custom_column_headers.filter(
      header => header.column_order_index <= maxColumnLimit
    );

    // eslint-disable-next-line no-param-reassign
    lot.custom_column_headers = limitedHeaders;

    // Filter custom column items for each line item to match retained headers
    lot.line_items.forEach(lineItem => {
      // eslint-disable-next-line no-param-reassign
      lineItem.custom_column_items = lineItem.custom_column_items?.filter(
        customColumnItem => {
          // Keep items that have a valid new_header_index and correspond to a retained header
          if (customColumnItem.new_header_index === undefined) return false;

          // Find the header this item belongs to by matching column_order_index
          const headerIndex = Number(customColumnItem.new_header_index);
          const correspondingHeader = limitedHeaders.find(
            h => h.column_order_index === headerIndex
          );

          // If found in limitedHeaders, keep the item
          return !!correspondingHeader;
        }
      ) || [];
    });
  });

  return importedLots;
};
